import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Paper,
  Grid,
  Link,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';
import { updateGeneralSettings } from '../../../services/storeSettingsService';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import { getPageInfoFromToken, testFacebookConnection } from '../../../services/facebookService';
import { clearStoreCache } from '../../../utils/storeDataLoader';

interface FacebookSettingsProps {
  settings: {
    pageId: string;
    accessToken: string;
    isEnabled: boolean;
    autoPostNewProducts: boolean;
    postTemplate?: string;
  };
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const FacebookSettings: React.FC<FacebookSettingsProps> = ({
  settings: initialSettings,
  onSave,
  onError,
  onSuccess
}) => {
  const { settings: fullSettings, refreshSettings } = useStoreSettings();
  const [formData, setFormData] = useState({ ...initialSettings });
  const [detecting, setDetecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [detectedPages, setDetectedPages] = useState<Array<{ id: string; name: string; accessToken: string }>>([]);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);

  // تحديث البيانات المحلية عند تغيير الإعدادات من المصدر
  React.useEffect(() => {
    setFormData({ ...initialSettings });
  }, [initialSettings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // كشف تلقائي للصفحات من الـ Access Token
  const handleAutoDetect = async () => {
    if (!formData.accessToken) {
      setStatusMessage({ type: 'warning', text: 'يرجى إدخال مفتاح الوصول (Access Token) أولاً' });
      return;
    }

    setDetecting(true);
    setStatusMessage(null);
    setDetectedPages([]);

    try {
      const result = await getPageInfoFromToken(formData.accessToken);
      if (result.success && result.pages.length > 0) {
        setDetectedPages(result.pages);
        setStatusMessage({ type: 'success', text: result.message });

        // إذا كانت صفحة واحدة فقط، اختارها تلقائياً
        if (result.pages.length === 1) {
          setFormData(prev => ({
            ...prev,
            pageId: result.pages[0].id,
            accessToken: result.pages[0].accessToken
          }));
          setStatusMessage({
            type: 'success',
            text: `✅ تم اختيار الصفحة تلقائياً: ${result.pages[0].name} (ID: ${result.pages[0].id})`
          });
        }
      } else {
        setStatusMessage({ type: 'error', text: result.message });
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'حدث خطأ أثناء الكشف التلقائي' });
    } finally {
      setDetecting(false);
    }
  };

  // اختيار صفحة من القائمة المكتشفة
  const handleSelectPage = (page: { id: string; name: string; accessToken: string }) => {
    setFormData(prev => ({
      ...prev,
      pageId: page.id,
      accessToken: page.accessToken
    }));
    setStatusMessage({
      type: 'success',
      text: `✅ تم اختيار: ${page.name} (ID: ${page.id})`
    });
    setDetectedPages([]);
  };

  // اختبار الاتصال
  const handleTestConnection = async () => {
    if (!formData.pageId || !formData.accessToken) {
      setStatusMessage({ type: 'warning', text: 'يرجى إدخال معرف الصفحة ومفتاح الوصول أولاً' });
      return;
    }

    setTesting(true);
    setStatusMessage(null);

    try {
      const result = await testFacebookConnection(formData.pageId, formData.accessToken);
      setStatusMessage({
        type: result.success ? 'success' : 'error',
        text: result.message
      });
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'حدث خطأ أثناء الاختبار' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    onSave(true);
    onError(null);
    try {
      if (!fullSettings) throw new Error('Settings not loaded');

      // مسح الكاش المحلي لضمان تحميل البيانات الجديدة
      clearStoreCache();

      await updateGeneralSettings({
        ...fullSettings.general,
        facebookIntegration: formData
      });

      await refreshSettings();
      setStatusMessage({ type: 'success', text: '✅ تم حفظ الإعدادات بنجاح!' });
      onSuccess(true);
    } catch (err: any) {
      console.error('Error saving Facebook settings:', err);
      onError(err.message || 'حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FacebookIcon color="primary" />
        الربط مع صفحة فيسبوك
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        قم بربط متجرك بصفحة فيسبوك الخاصة بك لنشر المنتجات تلقائياً وتسهيل وصول العملاء.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>الخطوات:</strong><br />
          1. اذهب إلى{' '}
          <Link href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener">
            Graph API Explorer
          </Link><br />
          2. اختر التطبيق الخاص بك (أو أنشئ واحداً).<br />
          3. في خانة <b>"User or Page"</b>، اختر <b>اسم صفحتك</b> (وليس User Token).<br />
          4. أضف صلاحية <b>pages_manage_posts</b> و <b>pages_read_engagement</b>.<br />
          5. اضغط <b>Generate Access Token</b> وانسخ الـ Token.<br />
          6. الصق الـ Token هنا واضغط <b>"كشف تلقائي"</b> وسيتم العثور على الصفحة تلقائياً.
        </Typography>
      </Alert>

      {statusMessage && (
        <Alert severity={statusMessage.type} sx={{ mb: 2 }} onClose={() => setStatusMessage(null)}>
          {statusMessage.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isEnabled}
                onChange={handleChange}
                name="isEnabled"
                color="primary"
              />
            }
            label="تفعيل الربط مع فيسبوك"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Page Access Token (مفتاح الوصول)"
            name="accessToken"
            value={formData.accessToken}
            onChange={handleChange}
            type="password"
            placeholder="الصق مفتاح الوصول هنا ثم اضغط كشف تلقائي..."
            disabled={!formData.isEnabled}
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAutoDetect}
              disabled={!formData.isEnabled || !formData.accessToken || detecting}
              startIcon={detecting ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ px: 3 }}
            >
              {detecting ? 'جاري الكشف...' : '🔍 كشف تلقائي للصفحة'}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={handleTestConnection}
              disabled={!formData.isEnabled || !formData.pageId || !formData.accessToken || testing}
              startIcon={testing ? <CircularProgress size={20} color="inherit" /> : <WifiIcon />}
            >
              {testing ? 'جاري الاختبار...' : '🔗 اختبار الاتصال'}
            </Button>
          </Box>
        </Grid>

        {/* قائمة الصفحات المكتشفة */}
        {detectedPages.length > 1 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                اختر الصفحة المطلوبة:
              </Typography>
              <List dense>
                {detectedPages.map((page) => (
                  <ListItem key={page.id} disablePadding>
                    <ListItemButton onClick={() => handleSelectPage(page)}>
                      <ListItemIcon>
                        <Radio checked={formData.pageId === page.id} />
                      </ListItemIcon>
                      <ListItemText
                        primary={page.name}
                        secondary={`Page ID: ${page.id}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Page ID (معرف الصفحة) - يتم تعبئته تلقائياً"
            name="pageId"
            value={formData.pageId}
            onChange={handleChange}
            placeholder="سيتم تعبئته تلقائياً عند الكشف..."
            disabled={!formData.isEnabled}
            helperText={formData.pageId ? `المعرف الحالي: ${formData.pageId}` : 'استخدم زر "كشف تلقائي" أعلاه'}
            InputProps={{
              endAdornment: formData.pageId ? <CheckIcon color="success" /> : null
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            قالب النشر على فيسبوك:
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              يمكنك استخدام المتغيرات التالية سيتم استبدالها تلقائياً بمعلومات المنتج:<br/>
              <b>{'{name}'}</b> = اسم المنتج | <b>{'{price}'}</b> = السعر | <b>{'{description}'}</b> = الوصف | <b>{'{link}'}</b> = رابط المنتج
            </Typography>
          </Alert>
          <TextField
            fullWidth
            label="النص الافتراضي للمنشور"
            name="postTemplate"
            value={formData.postTemplate || `🌟 منتج جديد متوفر الآن!\n📦 {name}\n💰 السعر: {price} ج.م\n📝 {description}\n\n🛒 اشتري الآن من خلال الرابط:\n{link}`}
            onChange={handleChange}
            multiline
            rows={6}
            disabled={!formData.isEnabled}
            placeholder="اكتب قالب النشر هنا..."
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.autoPostNewProducts}
                onChange={handleChange}
                name="autoPostNewProducts"
                color="secondary"
                disabled={!formData.isEnabled}
              />
            }
            label="نشر المنتجات الجديدة تلقائياً عند إضافتها"
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSave}
            size="large"
            sx={{ px: 4 }}
            startIcon={<FacebookIcon />}
          >
            💾 حفظ الإعدادات
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FacebookSettings;
